import React, { useState, useRef, useEffect, useCallback, MouseEvent } from 'react'
import {
  IconArrowsDiagonalMinimize,
  IconMapPin,
  IconRefresh,
  IconMaximize,
  IconMinimize,
  IconArrowNarrowRight,
  IconPlus,
  IconArrowUp,
  IconArrowDown,
  IconAlertCircle,
  IconGripHorizontal,
  // IconHistory,
} from '@tabler/icons-react'
import { useNavigate, useLocation, useParams } from 'react-router'
import Typography from '../atoms/Typography'
import { cn } from 'src/enviroment/lib/utils'
import { useChatStore } from 'src/enviroment/store/chatStore'
import { useOnboardingStore } from 'src/enviroment/store/onboardingStore'
import CopaLogo from '../../assets/logo-copa.svg?react'
import { sendProjectStreamingConversation, sendTenantStreamingConversation } from '../../enviroment/api/chat'
import { useChatSessions } from '../../enviroment/hooks/useChatSessions'
import { useStickToBottom } from '../../enviroment/hooks/useStickToBottom'
import { isPdfUrl } from 'src/enviroment/lib/fileUtils'
import PDFViewerModal from './dialogs/PDFViewerModal'
import { v4 as uuidv4 } from 'uuid'
import { ROUTES } from 'src/types/routes'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import useAuth from 'src/enviroment/hooks/useAuth'
// import SessionHistoryPanel from './ChatWidget/SessionHistoryPanel'
import ChatMessageComponent from './ChatWidget/ChatMessageComponent'
import extractFileName from '../../enviroment/utils/extractFileNameFromStr'
import transformToFullUrl from '../../enviroment/utils/transformStrToFullUrl'
import { UserRole } from '../../types/user'
import { useQueryClient } from '@tanstack/react-query'
import { projectRouterKeys } from 'src/api/routes/project'

const CONTAINER_URL = import.meta.env.VITE_APP_CONTAINER_URL

// Main component
const ChatWidget: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ id?: string }>()
  const queryClient = useQueryClient()
  const user = useAuth()
  const currentTenant = useTenantStore((s) => s.currentTenant)

  const isPublicProject = location.pathname.includes('landscapes/project')
  const isProjectPage = location.pathname.includes('project')
  const projectId = params.id

  const userId = user?.user?.id ?? ''
  const tenantId = currentTenant?.id ?? ''

  const [inputMessage, setInputMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const {
    selectedCountry,
    isProjectMode,
    sessionId,
    messages,
    setProjectMode,
    setSessionId,
    addMessage,
    updateLastAssistantMessage,
    updateLastAssistantThinking,
    setLastAssistantThinkingDone,
    removeLastAssistantMessage,
    addErrorMessage,
    clearSystemMessages,
    clearMessages,
    upsertMessageByType,
    isGenerating,
    stopGeneration,
  } = useChatStore()

  const {
    isLoadingSessions,
    loadLatestSession,
    createSessionWithWelcomeMessage,
    createTenantSessionSilent,
    createSessionWithMiomboWelcomeMessage,
  } = useChatSessions({
    userId,
    tenantId,
    projectId: isProjectMode ? projectId ?? undefined : undefined,
  })

  const isOnboardingOpen = useOnboardingStore((state) => state.isOnboardingOpen)

  const isExpanded = useChatStore((state) => state.isExpanded)
  const isFullScreen = useChatStore((state) => state.isFullScreen)
  const setIsExpanded = useChatStore((state) => state.setIsExpanded)
  const setIsFullScreen = useChatStore((state) => state.setIsFullScreen)
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false)
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState<boolean>(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfFileName, setPdfFileName] = useState<string>('Document')
  const DEFAULT_SIZE = { width: 620, height: 700 }
  const OFFSET = { x: 40, y: 48 }
  const MARGIN = { x: OFFSET.x * 2, y: OFFSET.y * 2 }
  const getInitialSize = () => ({
    width: Math.min(DEFAULT_SIZE.width, Math.max(window.innerWidth - MARGIN.x, 320)),
    height: Math.min(DEFAULT_SIZE.height, Math.max(window.innerHeight - MARGIN.y, 400)),
  })
  const [modalSize, setModalSize] = useState(getInitialSize)
  const [isResizing, setIsResizing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [modalPosition, setModalPosition] = useState<{ top?: number; bottom?: number; left?: number; right?: number }>({
    bottom: OFFSET.y,
    right: OFFSET.x,
  })

  const projectInitializedRef = useRef<string | null>(null)
  const { scrollRef: messagesScrollRef, isAtBottom, scrollToBottom, followIfAtBottom } = useStickToBottom()
  const prevLastMessageIdRef = useRef<string | undefined>(undefined)
  const createProjectFormRef = useRef<HTMLDivElement | null>(null)
  const modalRef = useRef<HTMLDivElement | null>(null)
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, right: 0 })
  const dragStartRef = useRef({ x: 0, y: 0, top: 0, left: 0 })
  const modalSizeRef = useRef(modalSize)
  useEffect(() => {
    modalSizeRef.current = modalSize
  }, [modalSize])

  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const showCTAs = !isProjectMode && messages.length === 0

  const resetTextareaHeight = ({ forceMinimum = false } = {}) => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      if (!forceMinimum) {
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
      }
    }
  }

  // Abort request
  const abortRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Abort generation started outside ChatWidget (e.g. proactive Copa response)
    stopGeneration()

    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === 'assistant' && lastMessage.isLoading) {
      removeLastAssistantMessage(lastMessage.id)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (isProjectPage && projectId && userId && tenantId) {
      setProjectMode(true)
      if (!sessionId && !isPublicProject) {
        loadLatestSession()
      }
    } else if (userId && tenantId) {
      setProjectMode(false)
      if (!sessionId) {
        loadLatestSession()
      }
    } else {
      setProjectMode(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectPage, isPublicProject, projectId, userId, tenantId])

  useEffect(() => {
    if (!isProjectPage) {
      projectInitializedRef.current = null
    }
  }, [isProjectPage])

  useEffect(() => {
    // Forgetting the last id while collapsed makes the next open count as new, so it scrolls smoothly.
    if (!isExpanded) {
      prevLastMessageIdRef.current = undefined
      return
    }

    const lastMessage = messages[messages.length - 1]
    const hasNewMessage = lastMessage?.id !== prevLastMessageIdRef.current
    prevLastMessageIdRef.current = lastMessage?.id

    if (lastMessage?.type === 'create-project') {
      createProjectFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    // Streamed chunks only follow a reader who stayed at the bottom; a new message always brings them back.
    if (hasNewMessage) scrollToBottom()
    else followIfAtBottom()
  }, [messages, isExpanded, scrollToBottom, followIfAtBottom])

  useEffect(() => {
    if (location.state?.keepWidgetOpen || (isProjectPage && location.pathname.includes('landscapes'))) {
      window.history.replaceState({}, '')
      setIsFullScreen(false)
    } else {
      setIsExpanded(false)
      setIsFullScreen(false)
    }
  }, [location.pathname, location.state])

  // Drag effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || isResizing || !modalRef.current) return

      const deltaX = e.clientX - dragStartRef.current.x
      const deltaY = e.clientY - dragStartRef.current.y

      const newTop = dragStartRef.current.top + deltaY
      const newLeft = dragStartRef.current.left + deltaX

      // Constrain to viewport bounds
      const maxTop = window.innerHeight - modalSize.height - 8
      const maxLeft = window.innerWidth - modalSize.width - 8
      const minTop = 8
      const minLeft = 8

      const constrainedTop = Math.max(minTop, Math.min(newTop, maxTop))
      const constrainedLeft = Math.max(minLeft, Math.min(newLeft, maxLeft))

      setModalPosition({
        top: constrainedTop,
        left: constrainedLeft,
        bottom: undefined,
        right: undefined,
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    if (isDragging) {
      document.body.style.cursor = 'move'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove as any)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove as any)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, isResizing, modalSize.height, modalSize.width])

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (isProjectMode) {
        sendProjectMessage(inputMessage)
      } else {
        sendTenantMessage(inputMessage)
      }
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value)
    resetTextareaHeight()
  }

  const handleMinimize = useCallback(() => setIsExpanded(false), [setIsExpanded])

  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()

    const linkText = e.currentTarget.textContent || 'Document'

    try {
      const fullUrl = transformToFullUrl(href, CONTAINER_URL)
      if (isPdfUrl(fullUrl)) {
        setPdfUrl(fullUrl)
        setPdfFileName(extractFileName(fullUrl) || linkText)
        setIsPdfViewerOpen(true)
      } else {
        window.open(fullUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error('Error processing link:', error)
      try {
        window.open(href, '_blank', 'noopener,noreferrer')
      } catch (e) {
        console.error('Failed to open link directly:', e)
      }
    }
  }, [])

  // Drag handlers
  const handleDragStart = (e: React.MouseEvent) => {
    if (isResizing) return // Don't drag while resizing
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)

    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect()
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        top: rect.top,
        left: rect.left,
      }
    }
  }

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    if (isDragging) return // Don't resize while dragging
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)

    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect()
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
        left: rect.left,
        right: window.innerWidth - rect.right,
      }
    }
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || isDragging || !modalRef.current) return

      const deltaX = e.clientX - resizeStartRef.current.x
      const deltaY = e.clientY - resizeStartRef.current.y

      const minWidth = Math.min(DEFAULT_SIZE.width, window.innerWidth - MARGIN.x)
      const minHeight = Math.min(DEFAULT_SIZE.height, window.innerHeight - MARGIN.y)
      const minMargin = 8
      const maxHeight = window.innerHeight - 16 // Account for vertical margins

      // Calculate new dimensions - dragging left (negative deltaX) increases width
      let newWidth = Math.max(resizeStartRef.current.width - deltaX, minWidth)
      const newHeight = Math.min(Math.max(resizeStartRef.current.height + deltaY, minHeight), maxHeight)

      // Calculate the right edge position (should stay fixed)
      const rightEdge = resizeStartRef.current.left + resizeStartRef.current.width

      // Calculate new left position to keep the right edge fixed
      let newLeft = rightEdge - newWidth

      // Constrain to viewport bounds
      if (newLeft < minMargin) {
        newLeft = minMargin
        newWidth = rightEdge - minMargin
      }

      // Max width constraint
      const maxWidth = window.innerWidth - minMargin - resizeStartRef.current.right
      if (newWidth > maxWidth) {
        newWidth = maxWidth
        newLeft = rightEdge - newWidth
      }

      // Update position to use left coordinate (keeps right edge fixed)
      setModalPosition((prev) => ({
        top: prev.top,
        bottom: prev.bottom,
        left: newLeft,
        right: undefined,
      }))

      setModalSize({ width: newWidth, height: newHeight })
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    if (isResizing) {
      document.body.style.cursor = 'nesw-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove as any)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove as any)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, isDragging])

  // Handle full screen toggle
  const handleFullScreenToggle = () => {
    setIsTransitioning(true)
    setIsFullScreen(!isFullScreen)
    // Reset transition state after a short delay
    setTimeout(() => {
      setIsTransitioning(false)
    }, 50)
  }

  // Sync store state - ensure widget is expanded when full screen is requested
  useEffect(() => {
    if (isFullScreen && !isExpanded) {
      setIsExpanded(true)
    }
  }, [isFullScreen, isExpanded, setIsExpanded])

  // Handle full screen state changes from store - check on mount and when state changes
  useEffect(() => {
    if (isFullScreen && isExpanded) {
      // Trigger transition when going to full screen
      setIsTransitioning(true)
      setTimeout(() => {
        setIsTransitioning(false)
      }, 50)
    }
  }, [isFullScreen, isExpanded])

  // On mount and location change, check URL parameter and store state for full screen
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const openCopa = searchParams.get('openCopa') === 'true'

    if (openCopa || isFullScreen) {
      // openCopa only opens the widget at its normal size; full screen stays user-driven
      if (!isExpanded) {
        setIsExpanded(true)
      }
      // Trigger transition
      setIsTransitioning(true)
      setTimeout(() => {
        setIsTransitioning(false)
      }, 50)

      // Remove the query parameter from URL
      if (openCopa) {
        const newSearch = new URLSearchParams(location.search)
        newSearch.delete('openCopa')
        const newUrl = location.pathname + (newSearch.toString() ? `?${newSearch.toString()}` : '')
        navigate(newUrl, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isFullScreen, isExpanded])

  useEffect(() => {
    resetTextareaHeight()
  }, [isFullScreen])

  // Re-clamp size and position when the browser window is resized
  useEffect(() => {
    const handleWindowResize = () => {
      if (isFullScreen) return
      const maxW = Math.max(window.innerWidth - 16, 320)
      const maxH = Math.max(window.innerHeight - 16, 400)
      setModalSize((prev) => ({
        width: Math.min(prev.width, maxW),
        height: Math.min(prev.height, maxH),
      }))
      setModalPosition((prev) => {
        if (prev.top !== undefined && prev.left !== undefined) {
          const { width, height } = modalSizeRef.current
          return {
            top: Math.max(8, Math.min(prev.top, window.innerHeight - height - 8)),
            left: Math.max(8, Math.min(prev.left, window.innerWidth - width - 8)),
            bottom: undefined,
            right: undefined,
          }
        }
        return prev
      })
    }
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [isFullScreen])

  const sendProjectMessage = async (content: string) => {
    if (!content.trim() || isLoading || !projectId || !userId || !tenantId) return

    setIsLoading(true)
    setInputMessage('')
    resetTextareaHeight({ forceMinimum: true })

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    const hadSession = !!sessionId

    addMessage({
      id: uuidv4(),
      role: 'user',
      content,
      date: new Date().toISOString(),
    })

    if (!hadSession) {
      await createSessionWithWelcomeMessage(abortControllerRef.current.signal)
      setIsLoading(false)
      abortControllerRef.current = null
      return
    }

    const assistantMessageId = uuidv4()
    addMessage({
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      date: new Date().toISOString(),
      isLoading: true,
    })

    try {
      await sendProjectStreamingConversation(
        {
          userId,
          tenantId,
          projectId,
          sessionId: sessionId,
          message: content,
        },
        {
          onChunk: (chunk: string) => {
            updateLastAssistantMessage(chunk)
          },
          onThinking: (step) => {
            updateLastAssistantThinking(step)
          },
          onThinkingDone: (durationMessage) => {
            setLastAssistantThinkingDone(durationMessage)
          },
          onSessionInfo: (sessionId: string) => {
            setSessionId(sessionId)
          },
          onDone: () => {
            setIsLoading(false)
          },
          onError: (error: Error) => {
            removeLastAssistantMessage(assistantMessageId)
            addErrorMessage(error)
            setIsLoading(false)
          },
        },
        abortControllerRef.current.signal,
      )
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.log('Request aborted')
        removeLastAssistantMessage(assistantMessageId)
      } else {
        console.error('Error sending project message', error)
        removeLastAssistantMessage(assistantMessageId)
        addErrorMessage(error)
      }
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
      // COPA may have added layers; a refetch lets the settled status poll see PROCESSING and resume.
      queryClient.invalidateQueries([projectRouterKeys.status, projectId])
    }
  }

  const sendTenantMessage = async (content: string) => {
    if (!content.trim() || isLoading || !userId || !tenantId) return

    setIsLoading(true)
    setInputMessage('')
    resetTextareaHeight({ forceMinimum: true })

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    const hadSession = !!sessionId

    addMessage({
      id: uuidv4(),
      role: 'user',
      content,
      date: new Date().toISOString(),
    })

    if (!hadSession) {
      await createSessionWithWelcomeMessage(abortControllerRef.current.signal)
      setIsLoading(false)
      abortControllerRef.current = null
      return
    }

    const assistantMessageId = uuidv4()
    addMessage({
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      date: new Date().toISOString(),
      isLoading: true,
    })

    try {
      await sendTenantStreamingConversation(
        {
          userId,
          tenantId,
          sessionId: sessionId,
          message: content,
        },
        {
          onChunk: (chunk: string) => {
            updateLastAssistantMessage(chunk)
          },
          onThinking: (step) => {
            updateLastAssistantThinking(step)
          },
          onThinkingDone: (durationMessage) => {
            setLastAssistantThinkingDone(durationMessage)
          },
          onSessionInfo: (sessionId: string) => {
            setSessionId(sessionId)
          },
          onDone: () => {
            setIsLoading(false)
          },
          onError: (error: Error) => {
            removeLastAssistantMessage(assistantMessageId)
            addErrorMessage(error)
            setIsLoading(false)
          },
        },
        abortControllerRef.current.signal,
      )
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.log('Request aborted')
        removeLastAssistantMessage(assistantMessageId)
      } else {
        console.error('Error sending tenant message', error)
        removeLastAssistantMessage(assistantMessageId)
        addErrorMessage(error)
      }
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }

  const handleRefreshClick = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    // The proactive Copa stream lives in the store, not in abortControllerRef
    stopGeneration()
    setIsLoading(false)

    clearMessages()

    if (isPublicProject) {
      await createSessionWithMiomboWelcomeMessage()
    } else if (isProjectMode) {
      abortControllerRef.current = new AbortController()
      await createSessionWithWelcomeMessage(abortControllerRef.current.signal)
      abortControllerRef.current = null
    } else {
      await createTenantSessionSilent()
    }
  }

  // CTA handlers
  const handleCreateProject = () => {
    upsertMessageByType('create-project')
  }

  const handleDiveIntoMiombo = () => {
    navigate(ROUTES.LANDSCAPES)
  }

  const handleExploreAtlas = () => {
    navigate(ROUTES.ATLAS)
    setIsExpanded(false)
  }

  const expandChatWidget = () => {
    clearSystemMessages()
    setIsExpanded(true)
    if (isPublicProject) {
      if (!sessionId) {
        createSessionWithMiomboWelcomeMessage()
      } else {
        upsertMessageByType('miombo-report')
      }
    } else if (isProjectMode && !sessionId) {
      createSessionWithWelcomeMessage()
    }
  }

  // Collapsed mode
  if (!isExpanded) {
    if (isOnboardingOpen) {
      return null
    }

    return (
      <div className="fixed bottom-12 right-10 z-50 flex items-center gap-3">
        <div
          className="relative"
          style={{
            padding: '1px', // border thickness
            borderRadius: '1000px',
            background: 'linear-gradient(216deg, #329175 -32.48%, #EFEFCF 132.11%)',
          }}
        >
          <div
            className="h-full w-full px-4 py-2"
            style={{
              background: '#0E0F12', // inner background color (important!)
              borderRadius: '1000px',
            }}
          >
            <p className="text-[14px] leading-[16px]" style={{ color: '#E5E7E8' }}>
              {isProjectMode ? 'Ask me for guidance' : "Not sure where to start? I'll guide you."}
            </p>
          </div>
        </div>

        <button
          onClick={expandChatWidget}
          className="transition-all duration-200 ease-in-out hover:scale-110 hover:animate-pulse"
        >
          <CopaLogo width={48} height={48} />
        </button>
      </div>
    )
  }

  return (
    <div
      className={cn('fixed z-50')}
      style={
        isFullScreen
          ? {
              // True fullscreen - cover everything
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }
          : {
              top: modalPosition.top !== undefined ? `${modalPosition.top}px` : undefined,
              bottom: modalPosition.bottom !== undefined ? `${modalPosition.bottom}px` : undefined,
              left: modalPosition.left !== undefined ? `${modalPosition.left}px` : undefined,
              right: modalPosition.right !== undefined ? `${modalPosition.right}px` : undefined,
            }
      }
    >
      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={isPdfViewerOpen}
        onClose={() => setIsPdfViewerOpen(false)}
        pdfUrl={pdfUrl}
        fileName={pdfFileName}
      />

      <div
        ref={modalRef}
        className={cn(
          'relative flex flex-col border border-gray-700 shadow-lg',
          isFullScreen ? 'h-full w-full bg-gray-900' : 'rounded-lg bg-gray-900/95',
          !isDragging && !isResizing && !isFullScreen && !isTransitioning && 'transition-all duration-300 ease-in-out',
          // a frame swallows mouse events, so crossing a report would lose the drag and its mouseup
          (isDragging || isResizing) && '[&_iframe]:pointer-events-none',
        )}
        style={
          isFullScreen
            ? { width: '100%', height: '100%' }
            : { width: `${modalSize.width}px`, height: `${modalSize.height}px` }
        }
        onMouseEnter={() => !isFullScreen && setIsHovered(true)}
        onMouseLeave={() => !isFullScreen && setIsHovered(false)}
      >
        {/* Resize handle - Bottom-left corner only - Hidden in full screen */}
        {!isFullScreen && (
          <div
            className={cn(
              'absolute bottom-0 left-0 z-10 cursor-nesw-resize transition-opacity duration-300 ease-in-out',
              isHovered || isResizing ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
            )}
            onMouseDown={handleResizeStart}
          >
            <div className="h-8 w-8 rounded-bl-lg border-b-[3px] border-l-[3px] border-[#CDD1D4]" />
          </div>
        )}
        {/* Header - Drag handle - Hidden in full screen */}
        {!isFullScreen && (
          <div
            className={cn(
              'pointer-events-none absolute z-20 flex w-full justify-center pt-[4px] transition-opacity duration-300 ease-in-out',
              isHovered || isDragging ? 'opacity-100' : 'opacity-0',
            )}
          >
            <IconGripHorizontal
              className="pointer-events-auto h-5 w-5 cursor-move"
              color="#F5F6F6"
              stroke={2}
              onMouseDown={handleDragStart}
            />
          </div>
        )}
        <div className="relative flex items-center justify-between px-[24px] pb-[14px] pt-[24px]">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center">
              <CopaLogo width={28} height={28} />
            </div>
            <Typography variant="base-semibold" className="ml-2 text-gray-100">
              Copa
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            {/* Selected country indicator */}
            {selectedCountry && (
              <div className="flex items-center gap-1 rounded-full px-2 py-1 text-sm text-gray-300">
                <IconMapPin className="h-4 w-4" stroke={1.5} />
                <span>{selectedCountry}</span>
              </div>
            )}

            {/* {isProjectMode && (
              <>
                <button
                  onClick={() => setShowSessionHistory(!showSessionHistory)}
                  className={cn(
                    'rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-gray-800',
                    showSessionHistory && 'bg-gray-800',
                  )}
                  title="Session history"
                  disabled={projectSessions.length === 0}
                >
                  <IconHistory className="h-5 w-5" stroke={2} color="#F5F6F6" />
                </button>
                <button
                  onClick={createNewSession}
                  className="rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-gray-800"
                  title="New session"
                >
                  <IconPlus className="h-5 w-5" stroke={2} color="#F5F6F6" />
                </button>
              </>
            )} */}

            <button
              onClick={handleRefreshClick}
              disabled={
                isPublicProject ? messages.length === 0 : isProjectMode ? messages.length === 1 : messages.length === 0
              }
              className="rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-gray-800"
              title="Clear chat"
            >
              <IconRefresh className="h-5 w-5" stroke={2} color="#F5F6F6" />
            </button>
            {!isFullScreen && (
              <button
                onClick={() => setIsExpanded(false)}
                className="flex items-center justify-center gap-2 rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-gray-800"
                title="Minimize"
              >
                <IconArrowsDiagonalMinimize className="h-5 w-5" color="#F5F6F6" stroke={2} />
                <Typography variant="sm" className="text-white">
                  Minimize
                </Typography>
              </button>
            )}

            <button
              onClick={handleFullScreenToggle}
              className="flex items-center justify-center gap-2 rounded-full p-1 transition-all duration-200 ease-in-out hover:bg-gray-800"
              title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
            >
              {isFullScreen ? (
                <>
                  <IconMinimize className="h-5 w-5" color="#F5F6F6" stroke={2} />
                  <Typography variant="sm" className="text-white">
                    Exit Full Screen
                  </Typography>
                </>
              ) : (
                <>
                  <IconMaximize className="h-5 w-5" color="#F5F6F6" stroke={2} />
                  <Typography variant="sm" className="text-white">
                    Full Screen
                  </Typography>
                </>
              )}
            </button>
          </div>
          <div className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-[#575D63]" />
        </div>

        {/* {isProjectMode && showSessionHistory && (
          <SessionHistoryPanel
            sessions={projectSessions}
            currentSessionId={projectContext?.sessionId ?? null}
            onSelectSession={selectSession}
            onDeleteSession={deleteSession}
          />
        )} */}
        {showCTAs && (
          <div className="p-[24px]">
            {user.userRole !== UserRole.VIEWER && (
              <div className="space-y-2">
                <button
                  onClick={handleCreateProject}
                  className="flex w-full items-start justify-between gap-4 rounded-[12px] border border-[#329175] bg-[#063728] px-5 py-4 text-left text-white transition-all duration-200 ease-in-out hover:brightness-110"
                >
                  <div className="flex flex-col">
                    <div className="flex items-center gap-4">
                      <Typography variant="lg-bold" className="text-[18px] leading-[28px]">
                        Create your Project
                      </Typography>
                    </div>
                    <Typography variant="sm" className="text-[14px] leading-[20px] text-[#E5E7E8]">
                      Start by drawing your area or uploading a file, then tell us your project type to keep things
                      moving.
                    </Typography>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center">
                    <IconArrowNarrowRight stroke={1} className="h-[40px] w-[40px]" />
                  </div>
                </button>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={handleDiveIntoMiombo}
                className="rounded-[8px] bg-[#131315] px-4 py-3 text-left text-white transition-all duration-200 ease-in-out hover:brightness-110"
              >
                <Typography variant="lg-bold" className="text-[18px] leading-[28px]">
                  Dive into Miombo
                </Typography>
                <Typography variant="sm" className="mt-1 text-[14px] leading-[20px] text-[#E5E7E8]">
                  Access our public Miombo layers and discover key insights about the landscape.
                </Typography>
              </button>

              <button
                onClick={handleExploreAtlas}
                className="rounded-[8px] bg-gradient-to-r from-[#131315] to-[#1B1C1E] px-4 py-3 text-left text-white transition-all duration-200 ease-in-out hover:brightness-110"
              >
                <Typography variant="lg-bold" className="text-[18px] leading-[28px]">
                  Explore the Atlas
                </Typography>
                <Typography variant="sm" className="mt-1 text-[14px] leading-[20px] text-[#E5E7E8]">
                  Dive into spatial layers and explore the regions you&apos;re working with.
                </Typography>
              </button>
            </div>
          </div>
        )}
        {/* Messages area */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={messagesScrollRef}
            className={cn(
              'flex-1 space-y-4 overflow-y-auto px-[24px] pb-[40px] pt-[24px] [mask-image:linear-gradient(to_bottom,black_calc(100%_-_32px),transparent)]',
            )}
          >
            {isLoadingSessions ? (
              <div className="flex h-full items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-700 border-t-emerald-400" />
                  <p className="text-sm text-gray-400">Loading session...</p>
                </div>
              </div>
            ) : (
              messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  {message.type === 'create-project' && index === messages.length - 1 && (
                    <div ref={createProjectFormRef} />
                  )}
                  <ChatMessageComponent
                    message={message as any}
                    isLast={index === messages.length - 1}
                    isStreaming={index === messages.length - 1 && (isLoading || isGenerating)}
                    onLinkClick={handleLinkClick}
                    onMinimize={handleMinimize}
                  />
                </React.Fragment>
              ))
            )}
          </div>
          {!isAtBottom && (
            <button
              onClick={() => scrollToBottom()}
              aria-label="Scroll to latest message"
              className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-gray-700 bg-gray-800 p-2 shadow-lg transition-all duration-200 ease-in-out hover:bg-gray-700"
            >
              <IconArrowDown className="h-4 w-4" color="#F5F6F6" stroke={2} />
            </button>
          )}
        </div>

        {/* Stop generation button */}
        {(isLoading || isGenerating) && (
          <div className="border-t border-gray-800 px-4 pt-2">
            <button
              onClick={abortRequest}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-800 px-3 py-1.5 text-gray-100 transition-all duration-200 ease-in-out hover:bg-gray-700"
            >
              <div className="h-2 w-2 border border-gray-100" />
              <span className="text-sm">Stop Generation</span>
            </button>
          </div>
        )}

        {/* Input area */}
        <div className="p-[24px]">
          <div className="flex flex-col gap-3">
            <div className="flex items-end gap-2 rounded-[8px] bg-copa-input px-4 py-3">
              <IconPlus className="mb-0.5 h-4 w-4 shrink-0 text-[#CDD1D4]" stroke={1.5} />
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                placeholder={isProjectMode ? "Ask about your project's data..." : 'Tell me how I can support you...'}
                className="flex max-h-[200px] min-h-[24px] flex-1 resize-none overflow-y-auto bg-transparent text-base leading-normal text-[#CDD1D4] placeholder-[#CDD1D4] focus:outline-none"
                rows={1}
              />
              <button
                onClick={() => (isProjectMode ? sendProjectMessage(inputMessage) : sendTenantMessage(inputMessage))}
                disabled={isLoading || !inputMessage.trim()}
                className="mb-0.5 shrink-0 transition-all duration-200 ease-in-out hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <IconArrowUp className="h-4 w-4 text-[#CDD1D4]" stroke={1.5} />
              </button>
            </div>
            <div className="flex items-center gap-1">
              <IconAlertCircle className="h-4 w-4 shrink-0 text-[#F5F6F6]" stroke={2} />
              <Typography variant="xs" className="text-[#F5F6F6]">
                Uses AI. Verify results.
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default ChatWidget
